import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import { useTranslation } from "react-i18next";
import API from "../../../Network/API";
import { ADD_MY_COURSE_SUCCESS, EDIT_MY_COURSE_SUCCESS } from "../../../Reducer/myCourseSlice";
import "./CourseForm.css";

// Upload limits. The video limit mirrors video_attach max:51200 (KB) in CourseStoreRequest;
// the image limit only applies to the picked file, since we upload a re-encoded JPEG crop.
const TITLE_MAX = 120;
const IMAGE_MAX_MB = 10;
const VIDEO_MAX_MB = 50;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

const TYPE_RECORDED = 1;
const COURSE_TYPES = [
    { id: 1, key: "recorded", icon: "fa-light fa-clapperboard-play" },
    { id: 2, key: "group", icon: "fa-light fa-users-class" },
    { id: 3, key: "one_to_one", icon: "fa-sharp fa-light fa-list-check" },
];
const TYPE_FROM_API = { recorded: 1, group: 2, "1-1": 3 };

// Which wizard step shows each field, so client and server errors can jump to it.
const FIELD_STEP = {
    course_type: 0,
    title: 1, category: 1, gender_id: 1, status: 1, description: 1, requirements: 1,
    image: 2, video_attach: 2,
    price: 3,
};

const MY_COURSES = "/dashboard/my-courses";

// CKEditor returns markup like "<p>&nbsp;</p>" for an empty-looking editor.
const hasContent = (html) =>
    /<(img|iframe|figure|table)/i.test(html || "") ||
    (html || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim() !== "";

const formatSize = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

function DropZone({ icon, title, hint, invalid, accept, onFile, disabled }) {
    const input = useRef(null);
    const [over, setOver] = useState(false);
    const open = () => !disabled && input.current?.click();

    return (
        <div
            role="button"
            tabIndex={disabled ? -1 : 0}
            className={`cf-drop ${over ? "is-over" : ""} ${invalid ? "is-invalid" : ""}`}
            onClick={open}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    open();
                }
            }}
            onDragOver={(e) => {
                e.preventDefault();
                setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
                e.preventDefault();
                setOver(false);
                if (!disabled) onFile(e.dataTransfer.files?.[0]);
            }}
        >
            <span className="cf-drop__icon"><i className={icon} /></span>
            <span className="cf-drop__title">{title}</span>
            <span className="cf-drop__hint">{hint}</span>
            <input
                ref={input}
                type="file"
                accept={accept}
                hidden
                onChange={(e) => {
                    onFile(e.target.files?.[0]);
                    e.target.value = "";
                }}
            />
        </div>
    );
}

// Shared create/edit wizard. Pass `course` to edit an existing course.
export default function CourseForm({ course }) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const categories = useSelector((state) => state.categoryReducer);
    const isEdit = Boolean(course);

    const [values, setValues] = useState(() => ({
        course_type: TYPE_FROM_API[course?.course_type] ?? TYPE_RECORDED,
        title: course?.title ?? "",
        category: course?.category_id ? String(course.category_id) : "",
        gender_id: String(course?.gender_id ?? 3),
        status: Number(course?.active) === 1 ? "1" : "0",
        price: course?.course_price != null ? String(course.course_price) : "0",
        description: course?.description ?? "",
        requirements: course?.requirements ?? "",
    }));
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState(null);
    const [step, setStep] = useState(0);
    const [maxStep, setMaxStep] = useState(isEdit ? 3 : 0);
    const [image, setImage] = useState(null); // object URL of the picked image, shown in the cropper
    const [cropper, setCropper] = useState(null);
    const [video, setVideo] = useState(null); // { file, url }
    const [feePct, setFeePct] = useState(0);
    const [feeFailed, setFeeFailed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [progress, setProgress] = useState(0);

    const formRef = useRef(null);
    const imageInput = useRef(null);
    const videoInput = useRef(null);
    const dirty = useRef(false);
    const mounted = useRef(true);
    // Editors take their data once, on mount; later changes live in `values`.
    const initialEditorData = useRef({ description: values.description, requirements: values.requirements }).current;

    const steps = useMemo(() => {
        const list = [
            { id: "type", label: t("courseForm.step_type") },
            { id: "info", label: t("courseForm.step_info") },
            { id: "media", label: t("courseForm.step_media") },
        ];
        if (values.course_type === TYPE_RECORDED) list.push({ id: "price", label: t("courseForm.step_price") });
        return list;
    }, [values.course_type, t]);
    const lastStep = steps.length - 1;
    const reachable = Math.min(maxStep, lastStep);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    useEffect(() => {
        API.get(`/v1/oauth/order_setting`).then((response) => {
            setFeePct(Number(response.data?.data?.order_fee) || 0);
        }).catch(() => {
            setFeeFailed(true);
        });
    }, []);

    useEffect(() => () => image && URL.revokeObjectURL(image), [image]);
    useEffect(() => () => video && URL.revokeObjectURL(video.url), [video]);

    // Closing the tab or reloading would lose the form (or abort an upload).
    useEffect(() => {
        const warn = (e) => {
            if (dirty.current || submitting) {
                e.preventDefault();
                e.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [submitting]);

    const money = (n) => {
        try {
            return new Intl.NumberFormat(i18n.resolvedLanguage || "en", { style: "currency", currency: "USD" }).format(n);
        } catch {
            return `$${n.toFixed(2)}`;
        }
    };

    const clearError = (name) => {
        setErrors((current) => {
            if (!(name in current)) return current;
            const { [name]: _removed, ...rest } = current;
            return rest;
        });
    };

    const setField = (name, value) => {
        dirty.current = true;
        setValues((v) => ({ ...v, [name]: value }));
        clearError(name);
    };

    const focusFirstError = () => {
        setTimeout(() => {
            const el = formRef.current?.querySelector(".cf-panel:not([hidden]) .is-invalid");
            if (!el) return;
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            el.focus?.({ preventScroll: true });
        }, 50);
    };

    const validateStep = (index) => {
        const e = {};
        switch (steps[index]?.id) {
            case "info":
                if (!values.title.trim()) e.title = t("courseForm.err_title_required");
                if (!values.category) e.category = t("courseForm.err_category_required");
                if (!hasContent(values.description)) e.description = t("courseForm.err_description_required");
                break;
            case "media":
                if (!image && !course?.cover_image) e.image = t("courseForm.err_thumbnail_required");
                if (!video && !course?.cover_video) e.video_attach = t("courseForm.err_video_required");
                break;
            case "price": {
                const price = Number(values.price);
                if (values.price === "" || !Number.isFinite(price) || price < 0) e.price = t("courseForm.err_price_invalid");
                break;
            }
            default:
        }
        return e;
    };

    // Moves to `target`, validating every step being skipped over. Going back never blocks.
    const goTo = (target) => {
        if (target <= step) {
            setStep(target);
            return true;
        }
        for (let i = step; i < target; i++) {
            const e = validateStep(i);
            if (Object.keys(e).length) {
                setErrors(e);
                setStep(i);
                focusFirstError();
                return false;
            }
        }
        setErrors({});
        setStep(target);
        setMaxStep((m) => Math.max(m, target));
        window.scrollTo({ top: 0, behavior: "smooth" });
        return true;
    };

    const pickImage = (file) => {
        if (!file) return;
        if (!IMAGE_TYPES.includes(file.type)) {
            setErrors((e) => ({ ...e, image: t("courseForm.err_image_type") }));
            return;
        }
        if (file.size > IMAGE_MAX_MB * 1024 * 1024) {
            setErrors((e) => ({ ...e, image: t("courseForm.err_image_size", { max: IMAGE_MAX_MB }) }));
            return;
        }
        dirty.current = true;
        setCropper(null);
        setImage(URL.createObjectURL(file));
        clearError("image");
    };

    const pickVideo = (file) => {
        if (!file) return;
        if (!file.type.startsWith("video/")) {
            setErrors((e) => ({ ...e, video_attach: t("courseForm.err_video_type") }));
            return;
        }
        if (file.size > VIDEO_MAX_MB * 1024 * 1024) {
            setErrors((e) => ({ ...e, video_attach: t("courseForm.err_video_size", { max: VIDEO_MAX_MB }) }));
            return;
        }
        dirty.current = true;
        setVideo({ file, url: URL.createObjectURL(file) });
        clearError("video_attach");
    };

    // 16:9 crop, capped at 1080p and flattened onto white so transparent PNGs don't turn black.
    const croppedThumbnail = () => new Promise((resolve) => {
        const canvas = cropper?.getCroppedCanvas({
            maxWidth: 1920,
            maxHeight: 1080,
            fillColor: "#fff",
            imageSmoothingQuality: "high",
        });
        if (!canvas) return resolve(null);
        canvas.toBlob(resolve, "image/jpeg", 0.9);
    });

    const showFieldErrors = (fieldErrors) => {
        setErrors(fieldErrors);
        const visible = Object.keys(fieldErrors).map((f) => FIELD_STEP[f] ?? 0).filter((i) => i <= lastStep);
        setStep(visible.length ? Math.min(...visible) : 0);
        toast.error(t("courseForm.fix_errors"));
        focusFirstError();
    };

    const handleServerError = (err) => {
        const response = err?.response;
        const fallback = isEdit ? t("courseForm.err_update_failed") : t("courseForm.err_create_failed");
        let message;

        if (!response) {
            message = t("courseForm.err_network");
        } else if (response.data?.errors && typeof response.data.errors === "object") {
            // Laravel validation: { field: ["message", ...] }
            const fieldErrors = {};
            Object.entries(response.data.errors).forEach(([field, messages]) => {
                fieldErrors[field] = Array.isArray(messages) ? messages[0] : String(messages);
            });
            const unknown = Object.keys(fieldErrors).filter((f) => !(f in FIELD_STEP));
            setSubmitError(unknown.length ? unknown.map((f) => fieldErrors[f]).join(" ") : null);
            showFieldErrors(fieldErrors);
            return;
        } else if (response.status === 401) {
            message = t("courseForm.err_session");
        } else if (response.status === 413) {
            message = t("courseForm.err_too_large");
        } else if (response.status >= 500) {
            // 5xx messages are raw exception text, not something to show tutors.
            message = fallback;
        } else {
            message = response.data?.message || fallback;
        }
        setSubmitError(message);
        toast.error(message);
    };

    const handleSave = async () => {
        if (submitting) return;
        setSubmitError(null);

        for (let i = 0; i <= lastStep; i++) {
            const e = validateStep(i);
            if (Object.keys(e).length) {
                showFieldErrors(e);
                return;
            }
        }

        setSubmitting(true);
        setProgress(0);
        try {
            const fd = new FormData();
            fd.append("title", values.title.trim());
            fd.append("description", values.description);
            fd.append("requirements", values.requirements);
            fd.append("category", values.category);
            fd.append("gender_id", values.gender_id);
            fd.append("course_type", values.course_type);
            fd.append("price", values.course_type === TYPE_RECORDED ? values.price : (course?.course_price ?? 0));
            if (isEdit) {
                fd.append("status", values.status);
                fd.append("_method", "PUT");
            }

            if (image) {
                const blob = await croppedThumbnail();
                if (!blob) {
                    showFieldErrors({ image: t("courseForm.err_image_process") });
                    return;
                }
                fd.append("image", blob, "thumbnail.jpg");
            }
            if (video) {
                fd.append("video_attach", video.file, video.file.name);
            }

            const url = isEdit ? `/v1/oauth/teacher/course/${course.id}/update` : "/v1/oauth/teacher/course/save";
            const response = await API.post(url, fd, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (e) => {
                    if (e.total && mounted.current) setProgress(Math.round((e.loaded * 100) / e.total));
                },
            });

            dirty.current = false;
            dispatch(isEdit ? EDIT_MY_COURSE_SUCCESS(response.data.data) : ADD_MY_COURSE_SUCCESS(response.data.data));
            toast.success(isEdit ? t("courseForm.updated") : t("courseForm.created"));
            navigate(MY_COURSES);
        } catch (err) {
            if (mounted.current) handleServerError(err);
        } finally {
            if (mounted.current) setSubmitting(false);
        }
    };

    const stepHasError = (index) => Object.keys(errors).some((f) => (FIELD_STEP[f] ?? 0) === index);
    const fieldClass = (name, base) => `${base} ${errors[name] ? "is-invalid" : ""}`;
    const fieldError = (name) => (errors[name] ? <div className="invalid-feedback d-block">{errors[name]}</div> : null);

    const price = Math.max(0, Number(values.price) || 0);
    const fee = (price * feePct) / 100;
    const panelHidden = (id) => steps[step]?.id !== id;
    const typeOf = COURSE_TYPES.find((type) => type.id === values.course_type);

    return (
        <div className="tutor-sign-in-section cf-page">
            <div className="container">
                <div className="cf-head">
                    <div>
                        <Link to={MY_COURSES} className="cf-backlink">
                            <i className="fa-light fa-arrow-left cf-flip" /> {t("courseForm.back_to_courses")}
                        </Link>
                        <h1 className="cf-title">{isEdit ? t("courseForm.edit_title") : t("courseForm.create_title")}</h1>
                        <p className="cf-sub">{isEdit ? course.title : t("courseForm.create_sub")}</p>
                    </div>
                    <span className="cf-stepcount">{t("courseForm.step_of", { current: step + 1, total: steps.length })}</span>
                </div>

                <ol className="cf-steps">
                    {steps.map((s, i) => {
                        const state = i === step ? "current" : i < step ? "done" : "upcoming";
                        return (
                            <li key={s.id} className={`cf-step is-${state} ${stepHasError(i) ? "has-error" : ""}`}>
                                <button
                                    type="button"
                                    onClick={() => goTo(i)}
                                    disabled={i > reachable || submitting}
                                    aria-current={i === step ? "step" : undefined}
                                >
                                    <span className="cf-step__dot">
                                        {stepHasError(i) ? <i className="fa-solid fa-exclamation" /> : state === "done" ? <i className="fa-solid fa-check" /> : i + 1}
                                    </span>
                                    <span className="cf-step__label">{s.label}</span>
                                </button>
                            </li>
                        );
                    })}
                </ol>

                {/* Every button is type="button": Enter in a field must not submit half a course. */}
                <form ref={formRef} className="cf-card" noValidate onSubmit={(e) => e.preventDefault()}>
                    <section className="cf-panel" hidden={panelHidden("type")}>
                        <div className="cf-panel__head">
                            <h2>{t("courseForm.type_heading")}</h2>
                            <p>{t("courseForm.type_sub")}</p>
                        </div>
                        <div className="cf-types" role="radiogroup" aria-label={t("courseForm.type_heading")}>
                            {COURSE_TYPES.map((type) => {
                                const active = values.course_type === type.id;
                                return (
                                    <button
                                        key={type.id}
                                        type="button"
                                        role="radio"
                                        aria-checked={active}
                                        className={`cf-type ${active ? "is-active" : ""}`}
                                        onClick={() => setField("course_type", type.id)}
                                    >
                                        <span className="cf-type__check"><i className="fa-solid fa-check" /></span>
                                        <span className="cf-type__icon"><i className={type.icon} /></span>
                                        <span className="cf-type__title">{t(`courseForm.type_${type.key}`)}</span>
                                        <span className="cf-type__desc">{t(`courseForm.type_${type.key}_desc`)}</span>
                                    </button>
                                );
                            })}
                        </div>
                        {fieldError("course_type")}
                    </section>

                    <section className="cf-panel" hidden={panelHidden("info")}>
                        <div className="cf-panel__head">
                            <h2>{t("courseForm.info_heading")}</h2>
                            <p>{t("courseForm.info_sub")}</p>
                        </div>

                        <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-end">
                                <label htmlFor="cf-title" className="form-label">
                                    {t("courseForm.title")} <span className="text-danger">*</span>
                                </label>
                                <span className="cf-counter">{values.title.length}/{TITLE_MAX}</span>
                            </div>
                            <input
                                id="cf-title"
                                type="text"
                                className={fieldClass("title", "form-control cf-input")}
                                placeholder={t("courseForm.title_placeholder")}
                                maxLength={TITLE_MAX}
                                value={values.title}
                                onChange={(e) => setField("title", e.target.value)}
                                aria-invalid={Boolean(errors.title)}
                            />
                            {fieldError("title")}
                        </div>

                        <div className="row g-3 mb-4">
                            <div className="col-md-6">
                                <label htmlFor="cf-category" className="form-label">
                                    {t("courseForm.category")} <span className="text-danger">*</span>
                                </label>
                                <select
                                    id="cf-category"
                                    className={fieldClass("category", "form-select cf-input")}
                                    value={values.category}
                                    onChange={(e) => setField("category", e.target.value)}
                                    aria-invalid={Boolean(errors.category)}
                                >
                                    <option value="">
                                        {categories?.data?.length ? t("courseForm.choose") : t("courseForm.no_categories")}
                                    </option>
                                    {categories?.data?.map((item) => (
                                        <option key={item.id} value={String(item.id)}>{item.name}</option>
                                    ))}
                                </select>
                                {fieldError("category")}
                            </div>
                            <div className="col-md-6">
                                <label htmlFor="cf-gender" className="form-label">{t("courseForm.audience")}</label>
                                <select
                                    id="cf-gender"
                                    className={fieldClass("gender_id", "form-select cf-input")}
                                    value={values.gender_id}
                                    onChange={(e) => setField("gender_id", e.target.value)}
                                >
                                    <option value="3">{t("courseForm.gender_both")}</option>
                                    <option value="1">{t("courseForm.gender_male")}</option>
                                    <option value="2">{t("courseForm.gender_female")}</option>
                                </select>
                                {fieldError("gender_id")}
                            </div>
                        </div>

                        {isEdit && (
                            <div className="cf-status mb-4">
                                <div className="form-check form-switch m-0">
                                    <input
                                        id="cf-status"
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        checked={values.status === "1"}
                                        onChange={(e) => setField("status", e.target.checked ? "1" : "0")}
                                    />
                                    <label htmlFor="cf-status" className="form-check-label fw-semibold">
                                        {values.status === "1" ? t("courseForm.published") : t("courseForm.unpublished")}
                                    </label>
                                </div>
                                <p className="cf-hint m-0">
                                    {values.status === "1" ? t("courseForm.status_hint_published") : t("courseForm.status_hint_unpublished")}
                                </p>
                                {fieldError("status")}
                            </div>
                        )}

                        <div className="mb-4">
                            <label className="form-label">
                                {t("courseForm.description")} <span className="text-danger">*</span>
                            </label>
                            <p className="cf-hint">{t("courseForm.description_hint")}</p>
                            <div className={fieldClass("description", "cf-editor")} tabIndex={-1}>
                                <CKEditor
                                    editor={ClassicEditor}
                                    data={initialEditorData.description}
                                    onChange={(_event, editor) => setField("description", editor.getData())}
                                />
                            </div>
                            {fieldError("description")}
                        </div>

                        <div>
                            <label className="form-label">
                                {t("courseForm.requirements")} <span className="cf-optional">{t("courseForm.optional")}</span>
                            </label>
                            <p className="cf-hint">{t("courseForm.requirements_hint")}</p>
                            <div className={fieldClass("requirements", "cf-editor")} tabIndex={-1}>
                                <CKEditor
                                    editor={ClassicEditor}
                                    data={initialEditorData.requirements}
                                    onChange={(_event, editor) => setField("requirements", editor.getData())}
                                />
                            </div>
                            {fieldError("requirements")}
                        </div>
                    </section>

                    <section className="cf-panel" hidden={panelHidden("media")}>
                        <div className="cf-panel__head">
                            <h2>{t("courseForm.media_heading")}</h2>
                            <p>{t("courseForm.media_sub")}</p>
                        </div>

                        <input
                            ref={imageInput}
                            type="file"
                            accept={IMAGE_TYPES.join(",")}
                            hidden
                            onChange={(e) => { pickImage(e.target.files?.[0]); e.target.value = ""; }}
                        />
                        <input
                            ref={videoInput}
                            type="file"
                            accept="video/*"
                            hidden
                            onChange={(e) => { pickVideo(e.target.files?.[0]); e.target.value = ""; }}
                        />

                        <div className="row g-4">
                            <div className="col-lg-6">
                                <div className="cf-media-label">
                                    {t("courseForm.thumbnail")} <span className="text-danger">*</span>
                                </div>
                                {image ? (
                                    <div className={fieldClass("image", "cf-media")} tabIndex={-1}>
                                        <Cropper
                                            className="cf-cropper"
                                            aspectRatio={16 / 9}
                                            src={image}
                                            viewMode={1}
                                            guides={true}
                                            background={false}
                                            autoCropArea={1}
                                            onInitialized={(instance) => setCropper(instance)}
                                        />
                                        <div className="cf-media__bar">
                                            <span className="cf-hint m-0">{t("courseForm.crop_hint")}</span>
                                            <div className="cf-media__actions">
                                                <button type="button" className="btn btn-sm cf-btn-ghost" onClick={() => imageInput.current?.click()}>
                                                    {t("courseForm.choose_another")}
                                                </button>
                                                <button type="button" className="btn btn-sm cf-btn-ghost" onClick={() => { setImage(null); setCropper(null); }}>
                                                    {t("courseForm.remove")}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ) : course?.cover_image ? (
                                    <div className={fieldClass("image", "cf-media")} tabIndex={-1}>
                                        <img className="cf-media__preview" src={course.cover_image} alt={t("courseForm.current_thumbnail")} />
                                        <div className="cf-media__bar">
                                            <span className="cf-hint m-0">{t("courseForm.current_thumbnail")}</span>
                                            <button type="button" className="btn btn-sm cf-btn-ghost" onClick={() => imageInput.current?.click()}>
                                                <i className="fa-light fa-arrows-rotate me-1" /> {t("courseForm.replace")}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <DropZone
                                        icon="fa-light fa-image"
                                        title={t("courseForm.drop_image")}
                                        hint={t("courseForm.image_hint", { max: IMAGE_MAX_MB })}
                                        accept={IMAGE_TYPES.join(",")}
                                        invalid={Boolean(errors.image)}
                                        onFile={pickImage}
                                        disabled={submitting}
                                    />
                                )}
                                {fieldError("image")}
                            </div>

                            <div className="col-lg-6">
                                <div className="cf-media-label">
                                    {t("courseForm.intro_video")} <span className="text-danger">*</span>
                                </div>
                                {video || course?.cover_video ? (
                                    <div className={fieldClass("video_attach", "cf-media")} tabIndex={-1}>
                                        <video
                                            key={video?.url ?? course.cover_video}
                                            className="cf-media__preview"
                                            controls
                                            preload="metadata"
                                            poster={video ? undefined : course?.cover_image}
                                            src={video?.url ?? course.cover_video}
                                        >
                                            {t("courseForm.video_not_supported")}
                                        </video>
                                        <div className="cf-media__bar">
                                            <span className="cf-hint m-0 text-truncate">
                                                {video ? `${video.file.name} · ${formatSize(video.file.size)}` : t("courseForm.current_video")}
                                            </span>
                                            <div className="cf-media__actions">
                                                <button type="button" className="btn btn-sm cf-btn-ghost" onClick={() => videoInput.current?.click()}>
                                                    {video ? t("courseForm.choose_another") : <><i className="fa-light fa-arrows-rotate me-1" /> {t("courseForm.replace")}</>}
                                                </button>
                                                {video && (
                                                    <button type="button" className="btn btn-sm cf-btn-ghost" onClick={() => setVideo(null)}>
                                                        {t("courseForm.remove")}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <DropZone
                                        icon="fa-light fa-film"
                                        title={t("courseForm.drop_video")}
                                        hint={t("courseForm.video_hint", { max: VIDEO_MAX_MB })}
                                        accept="video/*"
                                        invalid={Boolean(errors.video_attach)}
                                        onFile={pickVideo}
                                        disabled={submitting}
                                    />
                                )}
                                {fieldError("video_attach")}
                            </div>
                        </div>
                    </section>

                    {values.course_type === TYPE_RECORDED && (
                        <section className="cf-panel" hidden={panelHidden("price")}>
                            <div className="cf-panel__head">
                                <h2>{t("courseForm.price_heading")}</h2>
                                <p>{t("courseForm.price_sub")}</p>
                            </div>
                            <div className="row g-4">
                                <div className="col-md-6">
                                    <label htmlFor="cf-price" className="form-label">
                                        {t("courseForm.price_label")} <span className="text-danger">*</span>
                                    </label>
                                    <div className={`input-group cf-price ${errors.price ? "has-validation" : ""}`}>
                                        <span className="input-group-text">$</span>
                                        <input
                                            id="cf-price"
                                            type="number"
                                            min={0}
                                            step="0.01"
                                            inputMode="decimal"
                                            className={fieldClass("price", "form-control cf-input")}
                                            value={values.price}
                                            onChange={(e) => setField("price", e.target.value)}
                                            aria-invalid={Boolean(errors.price)}
                                        />
                                    </div>
                                    {fieldError("price")}
                                    <p className="cf-hint mt-2">{t("courseForm.price_hint")}</p>
                                </div>
                                <div className="col-md-6">
                                    <div className="cf-summary">
                                        <h3>{t("courseForm.price_summary")}</h3>
                                        <div className="cf-summary__row">
                                            <span>{t("courseForm.price_line")}</span>
                                            <span>{money(price)}</span>
                                        </div>
                                        <div className="cf-summary__row">
                                            <span>{t("courseForm.platform_fee")} ({feeFailed ? "—" : `${feePct}%`})</span>
                                            <span>{feeFailed ? "—" : `− ${money(fee)}`}</span>
                                        </div>
                                        <div className="cf-summary__row is-total">
                                            <span>{t("courseForm.you_will_earn")}</span>
                                            <span>{feeFailed ? "—" : money(price - fee)}</span>
                                        </div>
                                        {feeFailed && <p className="cf-hint mt-2 mb-0">{t("courseForm.fee_unavailable")}</p>}
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}

                    {submitError && (
                        <div className="alert alert-danger d-flex align-items-start gap-2 cf-alert" role="alert">
                            <i className="fa-light fa-circle-exclamation mt-1" />
                            <span>{submitError}</span>
                        </div>
                    )}

                    <div className="cf-footer">
                        {submitting ? (
                            <div className="cf-progress" role="status" aria-live="polite">
                                <div className="cf-progress__label">
                                    <span>{progress < 100 ? t("courseForm.uploading", { pct: progress }) : t("courseForm.processing")}</span>
                                    <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                                </div>
                                <div className="progress" style={{ height: 6 }}>
                                    <div
                                        className={`progress-bar ${progress >= 100 ? "progress-bar-striped progress-bar-animated" : ""}`}
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                            </div>
                        ) : (
                            <span className="cf-footer__type">
                                {typeOf && <><i className={typeOf.icon} /> {t(`courseForm.type_${typeOf.key}`)}</>}
                            </span>
                        )}

                        <div className="cf-footer__actions">
                            {step === 0 ? (
                                <Link to={MY_COURSES} className="btn cf-btn-ghost">{t("courseForm.cancel")}</Link>
                            ) : (
                                <button type="button" className="btn cf-btn-ghost" onClick={() => goTo(step - 1)} disabled={submitting}>
                                    {t("courseForm.back")}
                                </button>
                            )}
                            {step < lastStep && (
                                <button
                                    type="button"
                                    className={`btn ${isEdit ? "cf-btn-ghost" : "cf-btn-primary"}`}
                                    onClick={() => goTo(step + 1)}
                                    disabled={submitting}
                                >
                                    {t("courseForm.next")} <i className="fa-light fa-arrow-right ms-1 cf-flip" />
                                </button>
                            )}
                            {/* Editing can be saved from any step; creating needs every step visited. */}
                            {(isEdit || step === lastStep) && (
                                <button type="button" className="btn cf-btn-primary" onClick={handleSave} disabled={submitting}>
                                    {isEdit ? t("courseForm.save") : t("courseForm.create")}
                                </button>
                            )}
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
